import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useCallback, useState } from "react";
import { useTreinamentoCargoPagination } from "../hooks/TreinamentoCargo_cadastro/pagination";
import { processError } from "../../../../common/utils/base_error";
import { TreinamentoTreinamentoCargo } from "../../domain/models/treinamento_treinamento_cargo";
import { CargoTreinamentoCargo } from "../../domain/models/cargo_treinamento_cargo";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

/**
 * LoadCargosState
 */

type LoadCargosState = {
  idTreinamento?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useTreinamentoCargoCadastroViewModel
 */

export const useTreinamentoCargoCadastroViewModel = () => {
  const {
    rh: {
      getAllCargosForTreinamentosCargosUseCase,
      getAllTreinamentosForTreinamentosCargosUseCase,
      saveCargoTreinamentoUseCase,
    },
  } = useDependencyInjection();

  const { setPageSaving, pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [treinamentos, setTreinamentos] = useState<TreinamentoTreinamentoCargo[]>();
  const [cargos, setCargos] = useState<CargoTreinamentoCargo[]>();

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>();
  const [selectedTreinamento, setSelectedTreinamento] = useState<TreinamentoTreinamentoCargo>();

  const pagination = useTreinamentoCargoPagination();

  /**
   * Funções de carregamento
   */

  const loadTreinamentos = useCallback(async () => {
    try {
      const result = await getAllTreinamentosForTreinamentosCargosUseCase.getAllTreinamentos();
      setTreinamentos(result);
    } catch (e: unknown) {
      setTreinamentos([]);
      throw e;
    }
  }, [
    getAllTreinamentosForTreinamentosCargosUseCase,
  ]);

  const loadCargos = useCallback(async (funcState?: LoadCargosState) => {
    try {
      const filteredIdTreinamento = funcState?.idTreinamento ?? selectedTreinamento?.idTreinamento ?? 0;

      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllCargosForTreinamentosCargosUseCase.getAllCargos(
        filteredIdTreinamento,
        filteredPage,
        filteredRowsPerPage
      );

      setCargos(getAllCargosForTreinamentosCargosUseCase.items);
      setSelectionModel({
        type: 'include',
        ids: new Set(getAllCargosForTreinamentosCargosUseCase.items.filter(x => x.checked).map(x => x.idCargo)),
      });

      pagination.setRowsPerPage(getAllCargosForTreinamentosCargosUseCase.pageSize);
      pagination.setTotalItems(getAllCargosForTreinamentosCargosUseCase.totalItems);
    } catch (e: unknown) {
      setCargos([]);
      throw e;
    }
  }, [
    selectedTreinamento,
    pagination,
    getAllCargosForTreinamentosCargosUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);

      await loadTreinamentos();

      if (selectedTreinamento) {
        await loadCargos();
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar treinamentos!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      setSaving(true);
      setPageSaving(true);

      if (selectedTreinamento && selectionModel) {
        const cargoIds: number[] = [];

        for (var value of selectionModel.ids.values()) {
          if (typeof value === 'number') {
            cargoIds.push(value as number);
          } else if (typeof value === 'string') {
            cargoIds.push(Number(value));
          }
        }

        await saveCargoTreinamentoUseCase.saveCargoTreinamento(
          selectedTreinamento.idTreinamento,
          cargoIds,
        );

        await loadTreinamentos();
        await loadCargos();

        pushInfoNotification("Sucesso", "Alterações salvas com sucesso!", { isAutoFinish: true });
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setSaving(false);
      setLoading(false);
      setPageSaving(false);
    }
  }, [
    selectedTreinamento,
    selectionModel,
    saveCargoTreinamentoUseCase,
    loadTreinamentos,
    loadCargos,
    setPageSaving,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadTreinamentos();

      if (selectedTreinamento) {
        await loadCargos();
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    selectedTreinamento,
    loadTreinamentos,
    loadCargos,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useTreinamentoCargoPagination.getState().setPage(page + 1);
      await loadCargos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCargos,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useTreinamentoCargoPagination.getState().setPage(1);
      useTreinamentoCargoPagination.getState().setRowsPerPage(rowsPerPage);
      await loadCargos({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCargos,
    pushErrorNotification,
  ]);

  const handleCargoRowSelectionChange = useCallback((models: GridRowSelectionModel) => {
    setSelectionModel(models);
  }, []);

  const handleTreinamentoSelect = useCallback(async (treinamento: TreinamentoTreinamentoCargo | null) => {
    try {
      setLoading(true);
      if (treinamento) {
        useTreinamentoCargoPagination.getState().setPage(1);
        setSelectedTreinamento(treinamento);
        await loadCargos({ idTreinamento: treinamento.idTreinamento, page: 1 });
      } else {
        setSelectedTreinamento(undefined);
        setCargos(undefined);
        setSelectionModel({ type: 'include', ids: new Set([]) });
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCargos,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    treinamentos,
    cargos,
    selectionModel,
    selectedTreinamento,
    pagination,
    handleSubmit,
    handleReload,
    handlePageChange,
    handleRowsPerPageChange,
    handleCargoRowSelectionChange,
    handleTreinamentoSelect,
  };
}